import { Button } from "components/DesignSystem/Button/Button";
import Input, { BareInput } from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useSubmitAlreadyRegisteredMutation } from "store/apis/salesTaxRegistration";
import { BackendError } from "types/utils/error";
import * as Yup from "yup";

type AlreadyRegisteredValues = {
  license_number: string;
  registration_license: File | null;
};

const alreadyRegisteredSchema = Yup.object({
  license_number: Yup.string().required("License number is required"),
  registration_license: Yup.mixed().required("Please upload a document"),
});

const initialValues: AlreadyRegisteredValues = {
  license_number: "",
  registration_license: null,
};

type AlreadyRegisteredModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  stateId: string;
  stateName: string;
  onSuccess?: () => void;
  onRegistered?: (stateId: string, stateName: string) => void;
};

export const AlreadyRegisteredModal = ({
  isOpen,
  onClose,
  entityId,
  stateId,
  stateName,
  onSuccess,
  onRegistered,
}: AlreadyRegisteredModalProps) => {
  const { alertToast, successToast } = useToast();

  const [submitAlreadyRegistered, { isLoading }] =
    useSubmitAlreadyRegisteredMutation();

  const handleSubmit = async (values: AlreadyRegisteredValues) => {
    if (!values.registration_license) return;

    try {
      await submitAlreadyRegistered({
        entityId,
        stateId,
        payload: {
          tax_detail_id: values.license_number,
          registration_license: values.registration_license,
          status: "REGISTERED",
        },
      }).unwrap();
      successToast({ message: "Registration details saved" });
      onSuccess?.();
      onClose();
      onRegistered?.(stateId, stateName);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="regular" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Provide Registration Details</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <FormikForm
          initialValues={initialValues}
          validationSchema={alreadyRegisteredSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <>
              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-4">
                  <BareInput
                    label="State"
                    value={stateName}
                    disabled
                    readOnly
                  />
                  <Input
                    withForm
                    label="Existing Tax Account ID"
                    name="license_number"
                    placeholder="0123456789"
                  />
                  <FileInput
                    name="registration_license"
                    withForm
                    label="Upload Registration License/Document"
                    file={
                      values.registration_license
                        ? {
                            uuid: "local",
                            name: values.registration_license.name,
                            file_type: "PDF",
                            is_previewable: false,
                          }
                        : null
                    }
                    onDelete={() => setFieldValue("registration_license", null)}
                    accept={{
                      "application/pdf": [".pdf"],
                      "image/jpeg": [".jpg", ".jpeg"],
                      "image/png": [".png"],
                    }}
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  Save Details
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
